/**
 * Manic Panic Color Care Bundle PDP — V2
 * File: assets/color-care-bundle.js
 *
 * Visible interface:
 * - Custom Shopify selectors, product cards, Color DNA, summary and CTA.
 *
 * Hidden engine:
 * - Rebuy Bundle Builder supplies eligible products, inventory, pricing,
 *   Shopify Functions discounting and the final add-bundle-to-cart action.
 */

(() => {
  'use strict';

  const SECTION_SELECTOR = '.mp-color-care-bundle';
  const REBUY_BUILDER_SELECTOR = '.rebuy-bundle-builder__main';
  const INIT_TIMEOUT_MS = 25000;
  const POLL_INTERVAL_MS = 250;
  const REBUY_SETTLE_MS = 220;

  const sleep = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const normalizeTitle = (value = '') =>
    String(value)
      .replace(/&amp;/gi, '&')
      .replace(/[™®©]/g, '')
      .replace(/[’‘`]/g, "'")
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();

  const formatMoney = (amount) => {
    if (!Number.isFinite(amount)) return '—';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const parseMoney = (value = '') => {
    const parsed = Number.parseFloat(
      String(value).replace(/[^0-9.-]+/g, '')
    );

    return Number.isFinite(parsed) ? parsed : null;
  };

  const debounce = (callback, delay = 100) => {
    let timer;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  class ColorCareBundleController {
    constructor(section) {
      this.section = section;
      this.config = this.readConfig();

      this.classicSelect = section.querySelector('[data-mp-classic-select]');
      this.loveSelect = section.querySelector('[data-mp-love-select]');
      this.customCTA = section.querySelector('[data-mp-add-bundle]');
      this.status = section.querySelector('[data-mp-bundle-status]');

      this.classicCard = section.querySelector('[data-mp-classic-card]');
      this.classicImage = section.querySelector('[data-mp-classic-image]');
      this.classicTitle = section.querySelector('[data-mp-classic-title]');
      this.classicDescriptor = section.querySelector(
        '[data-mp-classic-descriptor]'
      );
      this.classicChips = section.querySelector('[data-mp-classic-chips]');

      this.loveCard = section.querySelector('[data-mp-love-card]');
      this.loveImage = section.querySelector('[data-mp-love-image]');
      this.loveTitle = section.querySelector('[data-mp-love-title]');
      this.loveDescriptor = section.querySelector(
        '[data-mp-love-descriptor]'
      );
      this.matchBadge = section.querySelector('[data-mp-match-badge]');
     this.matchHeading = section.querySelector('[data-mp-match-heading]');
     
      this.summaryProducts = section.querySelector(
        '[data-mp-summary-products]'
      );
      this.bundlePrice = section.querySelector('[data-mp-bundle-price]');
      this.comparePrice = section.querySelector('[data-mp-compare-price]');

      this.engineMount = section.querySelector('[data-mp-rebuy-engine]');
      this.builder = null;
      this.classicStep = null;
      this.loveStep = null;
      this.rebuySummary = null;
      this.rebuyCTA = null;

      this.classicProducts = [];
      this.loveProducts = [];

      this.selectedClassic = null;
      this.selectedLove = null;
      this.recommendedLoveTitle = '';

      this.isSyncing = false;
      this.isReady = false;
      this.observer = null;
      this.startedAt = Date.now();

      this.handleMutations = debounce(this.syncFromRebuy.bind(this), 120);
      this.handleClassicChange = this.handleClassicChange.bind(this);
      this.handleLoveChange = this.handleLoveChange.bind(this);
      this.handleCustomCTA = this.handleCustomCTA.bind(this);
    }

    readConfig() {
      const script = this.section.querySelector('[data-mp-bundle-config]');

      if (!script) {
        console.error('[MP Bundle] Configuration JSON was not found.');
        return {};
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[MP Bundle] Configuration JSON is invalid.', error);
        return {};
      }
    }

    getDiscountPercent() {
      const configured = Number(this.config?.discountPercent);
      return Number.isFinite(configured) ? configured : 20;
    }

    async initialize() {
      this.setStatus('Loading your color matches…');
      await this.waitForRebuy();

      if (!this.builder) {
        this.setStatus(
          'The bundle builder could not load. Please refresh the page.'
        );
        return;
      }

      this.captureRebuyElements();
      await this.waitForProducts();

      /*
       * Rebuy can restore a previous/default selection before our custom
       * interface starts. Clear any selected Rebuy items first so the hidden
       * builder and visible selectors start from the same empty state.
       */
      await this.clearInitialRebuySelections();

      /*
       * Re-capture after Rebuy settles, then verify each option against
       * Shopify inventory. Sold-out products are removed from the consumer
       * selectors without disabling the rest of the bundle.
       */
      this.captureRebuyElements();
      this.captureProducts();
      await this.refreshShopifyAvailability();

      if (!this.classicProducts.length || !this.loveProducts.length) {
        this.setStatus(
          'No in-stock bundle combinations are available right now.'
        );
        console.error(
          '[MP Bundle] No in-stock Classic and/or Love Color products were found.'
        );
        return;
      }

      this.populateSelectors();
      this.bindEvents();
      this.observeRebuy();
      this.isReady = true;

      /* Rebuy has loaded and its available product data has been captured. */
      this.section.classList.add('mp-bundle-v2-ready');

      await this.applyDefaults();
      this.syncFromRebuy();

      /*
       * iOS Safari can occasionally retain a third-party body lock after the
       * hidden Rebuy builder initializes. Reconcile once after initialization.
       */
      window.setTimeout(() => {
        this.releaseOrphanedBundleScrollLock();
      }, 300);
    }

    async waitForRebuy() {
      while (Date.now() - this.startedAt < INIT_TIMEOUT_MS) {
        this.builder =
          this.engineMount?.querySelector(REBUY_BUILDER_SELECTOR) ||
          this.engineMount?.querySelector('.rebuy-bundle-builder') ||
          null;

        if (this.builder) return;

        await sleep(POLL_INTERVAL_MS);
      }
    }
    async waitForProducts() {
  const productsStartedAt = Date.now();

  while (Date.now() - productsStartedAt < INIT_TIMEOUT_MS) {
    this.captureRebuyElements();
    this.captureProducts();

    if (
      this.classicProducts.length > 0 &&
      this.loveProducts.length > 0
    ) {
      return;
    }

    await sleep(POLL_INTERVAL_MS);
  }
}

    captureRebuyElements() {
      this.classicStep =
        this.builder.querySelector(
          '.rebuy-bundle-builder__step-container-0'
        ) ||
        this.builder.querySelectorAll(
          '.rebuy-bundle-builder__step-container'
        )[0] ||
        null;

      this.loveStep =
        this.builder.querySelector(
          '.rebuy-bundle-builder__step-container-1'
        ) ||
        this.builder.querySelectorAll(
          '.rebuy-bundle-builder__step-container'
        )[1] ||
        null;

      this.rebuySummary = this.builder.querySelector(
        '.rebuy-bundle-builder__group-container'
      );

      this.rebuyCTA = this.builder.querySelector(
        '.rebuy-bundle-builder__cta-container button.rebuy-button'
      );
    }

    captureProducts() {
      this.classicProducts = this.readProductsFromStep(
        this.classicStep,
        'Classic High Voltage®'
      );

      this.loveProducts = this.readProductsFromStep(
        this.loveStep,
        'Love Color™'
      );
    }

    readProductsFromStep(step, typeLabel) {
      if (!step) return [];

      return [
        ...step.querySelectorAll(
          '.rebuy-product-block .rebuy-bundle-builder__step-product'
        ),
      ]
        .map((productElement) =>
          this.createProductRecord(productElement, typeLabel)
        )
        .filter((product) => product.title);
    }

    readRebuyAvailability(productElement) {
      if (!productElement) return true;

      const block =
        productElement.closest('.rebuy-product-block') || productElement;

      const statusText = `${block.textContent || ''}`.toLowerCase();
      const classText = `${block.className || ''}`.toLowerCase();

      if (
        statusText.includes('sold out') ||
        statusText.includes('out of stock') ||
        statusText.includes('unavailable') ||
        classText.includes('sold-out') ||
        classText.includes('sold_out') ||
        classText.includes('out-of-stock') ||
        block.getAttribute('data-available') === 'false'
      ) {
        return false;
      }

      const buttons = [
        ...block.querySelectorAll(
          '.rebuy-bundle-builder__product-actions button'
        ),
      ];

      const soldOutButton = buttons.find((button) => {
        const label = `${button.getAttribute('aria-label') || ''} ${
          button.textContent || ''
        }`.toLowerCase();

        return (
          label.includes('sold out') ||
          label.includes('out of stock') ||
          label.includes('unavailable')
        );
      });

      return !soldOutButton;
    }

    async checkShopifyAvailability(product) {
      if (!product || product.available === false) return false;

      if (!product.url) {
        return product.available !== false;
      }

      try {
        const productUrl = new URL(product.url, window.location.origin);

        if (productUrl.origin !== window.location.origin) {
          return product.available !== false;
        }

        let pathname = productUrl.pathname.replace(/\/$/, '');

        if (!pathname.endsWith('.js')) {
          pathname = `${pathname}.js`;
        }

        const response = await fetch(pathname, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Product request failed with ${response.status}.`);
        }

        const shopifyProduct = await response.json();

        if (typeof shopifyProduct.available === 'boolean') {
          return shopifyProduct.available;
        }

        if (Array.isArray(shopifyProduct.variants)) {
          return shopifyProduct.variants.some(
            (variant) => variant && variant.available
          );
        }

        return product.available !== false;
      } catch (error) {
        console.warn(
          `[MP Bundle] Could not verify Shopify inventory for ${product.title}.`,
          error
        );

        /*
         * Fail open to Rebuy availability if the Shopify Ajax request is
         * unavailable so one network issue cannot disable the whole bundle.
         */
        return product.available !== false;
      }
    }

    async refreshShopifyAvailability() {
      const allProducts = [
        ...this.classicProducts,
        ...this.loveProducts,
      ];

      await Promise.all(
        allProducts.map(async (product) => {
          product.available = await this.checkShopifyAvailability(product);
        })
      );

      this.classicProducts = this.classicProducts.filter(
        (product) => product.available
      );

      this.loveProducts = this.loveProducts.filter(
        (product) => product.available
      );
    }

    createProductRecord(productElement, typeLabel) {
      const block =
        productElement.closest('.rebuy-product-block') || productElement;

      const titleElement = productElement.querySelector(
        '.rebuy-product-title a, .rebuy-product-title'
      );

      const imageElement = productElement.querySelector(
        '.rebuy-product-image img'
      );

      const salePriceElement = productElement.querySelector(
        '.rebuy-product-price .rebuy-money.sale'
      );

      const regularPriceElement = productElement.querySelector(
        '.rebuy-product-price .rebuy-money:not(.compare-at):not(.sale)'
      );

      const comparePriceElement = productElement.querySelector(
        '.rebuy-product-price .rebuy-money.compare-at'
      );

      const addButton = productElement.querySelector(
        '.rebuy-bundle-builder__product-actions button.rebuy-button'
      );

      const title = titleElement?.textContent?.replace(/\s+/g, ' ').trim() || '';
      const priceText =
        salePriceElement?.textContent ||
        regularPriceElement?.textContent ||
        '';

      return {
        key: normalizeTitle(title),
        title,
        typeLabel,
        available: this.readRebuyAvailability(productElement),
        image: imageElement?.currentSrc || imageElement?.src || '',
        imageAlt: imageElement?.alt || title,
        url: titleElement?.href || '',
        price: parseMoney(priceText),
        priceText: priceText.replace(/\s+/g, ' ').trim(),
        compareAtPrice: parseMoney(comparePriceElement?.textContent || ''),
        block,
        element: productElement,
        addButton,
      };
    }

    populateSelectors() {
      this.populateSelect(
        this.classicSelect,
        this.classicProducts,
        'Choose your Classic shade'
      );

      this.populateLoveSelector();
    }

    populateSelect(select, products, placeholder) {
      if (!select) return;

      select.innerHTML = '';

      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.disabled = true;
      placeholderOption.selected = true;
      select.appendChild(placeholderOption);

      products.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.title;
        option.textContent = product.title;
        select.appendChild(option);
      });
    }

    populateLoveSelector() {
      if (!this.loveSelect) return;

      const existingValue = this.loveSelect.value;
      this.loveSelect.innerHTML = '';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Love Color™';
      placeholder.disabled = true;
      placeholder.selected = !existingValue;
      this.loveSelect.appendChild(placeholder);

      const orderedProducts = [...this.loveProducts].sort((a, b) => {
        const aRecommended =
          normalizeTitle(a.title) === normalizeTitle(this.recommendedLoveTitle);
        const bRecommended =
          normalizeTitle(b.title) === normalizeTitle(this.recommendedLoveTitle);

        if (aRecommended && !bRecommended) return -1;
        if (!aRecommended && bRecommended) return 1;
        return 0;
      });

      orderedProducts.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.title;

        const isRecommended =
          normalizeTitle(product.title) ===
          normalizeTitle(this.recommendedLoveTitle);

        option.textContent = isRecommended
          ? `${product.title} — Best Match`
          : product.title;

        this.loveSelect.appendChild(option);
      });

      if (
        existingValue &&
        this.findProduct(this.loveProducts, existingValue)
      ) {
        this.loveSelect.value = existingValue;
      }

      if (this.selectedClassic) {
        this.loveSelect.disabled = this.loveProducts.length === 0;
      }
    }

    bindEvents() {
      this.classicSelect?.addEventListener(
        'change',
        this.handleClassicChange
      );

      this.loveSelect?.addEventListener('change', this.handleLoveChange);
      this.customCTA?.addEventListener('click', this.handleCustomCTA);
    }

    observeRebuy() {
      this.observer = new MutationObserver(this.handleMutations);

      this.observer.observe(this.builder, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'class',
          'disabled',
          'aria-label',
          'aria-selected',
          'aria-pressed',
        ],
      });
    }

    async clearInitialRebuySelections() {
      if (!this.builder) return;

      const buttons = [
        ...this.builder.querySelectorAll(
          '.rebuy-bundle-builder__product-actions button, ' +
          '.rebuy-bundle-builder__group-container button'
        ),
      ];

      const removeButtons = buttons.filter((button) => {
        if (!button || button.disabled) return false;

        const label = `${button.getAttribute('aria-label') || ''} ${
          button.textContent || ''
        }`.toLowerCase();

        return (
          label.includes('remove') ||
          label.includes('decrease') ||
          label.includes('minus')
        );
      });

      for (const button of removeButtons) {
        button.click();
        await sleep(REBUY_SETTLE_MS);
      }

      this.captureRebuyElements();
      this.captureProducts();
      this.releaseOrphanedBundleScrollLock();
    }

    async applyDefaults() {
      // Do not preselect a Classic or Love Color product.
      this.selectedClassic = null;
      this.selectedLove = null;
      this.recommendedLoveTitle = '';

      // Reset both custom selectors to their placeholder options.
      if (this.classicSelect) {
        this.classicSelect.value = '';
      }

      if (this.loveSelect) {
        this.loveSelect.value = '';
        this.loveSelect.disabled = true;
      }

      // Hide the selected-product cards until the shopper chooses a Classic shade.
      if (this.classicCard) {
        this.classicCard.hidden = true;
      }

      if (this.loveCard) {
        this.loveCard.hidden = true;
      }

      if (this.matchHeading) {
        this.matchHeading.textContent = 'Recommended Match Appears Here';
      }

      // Refresh the summary so it does not show default products.
      this.renderSummary();

      // Clear any initialization message.
      this.setStatus('');
    }

    async handleClassicChange(event) {
      await this.selectClassic(event.target.value, true);
    }

    async handleLoveChange(event) {
      await this.selectLove(event.target.value, false);
    }

    async selectClassic(title, autoSelectLove) {
      const product = this.findProduct(this.classicProducts, title);
      if (!product || this.isSyncing) return;

      this.isSyncing = true;
      this.setStatus('Updating your Classic shade…');

      try {
        await this.selectRebuyProduct(
          this.classicStep,
          product,
          this.selectedClassic
        );

        this.selectedClassic = product;
        this.classicSelect.value = product.title;
        this.renderClassicCard(product);
        if (this.matchHeading) {
  this.matchHeading.textContent = 'We Found Your Match';
}

        const recommendation = this.getRecommendation(product.title);
        this.recommendedLoveTitle = recommendation?.match || '';
        this.populateLoveSelector();

        if (autoSelectLove && this.recommendedLoveTitle) {
          const recommendedProduct = this.findProduct(
            this.loveProducts,
            this.recommendedLoveTitle
          );

          if (recommendedProduct) {
            await this.selectRebuyProduct(
              this.loveStep,
              recommendedProduct,
              this.selectedLove
            );

            this.selectedLove = recommendedProduct;
            this.loveSelect.value = recommendedProduct.title;
            this.renderLoveCard(recommendedProduct, recommendation);
          } else {
            /*
             * The configured best match is currently sold out. Do not fail the
             * whole bundle; let the shopper choose another available Love Color.
             */
            if (this.selectedLove) {
              await this.removeRebuyProduct(this.selectedLove);
            }

            this.selectedLove = null;
            this.loveSelect.value = '';

            if (this.loveCard) {
              this.loveCard.hidden = true;
            }

            this.setStatus(
              'Your best Love Color match is temporarily sold out. Choose another available Love Color.'
            );
          }
        }

        this.renderSummary();
        await sleep(REBUY_SETTLE_MS);
        this.syncFromRebuy();
      } finally {
        this.isSyncing = false;
      }
    }

    async selectLove(title, isAutomatic = false) {
      const product = this.findProduct(this.loveProducts, title);
      if (!product || this.isSyncing) return;

      this.isSyncing = true;
      this.setStatus(
        isAutomatic
          ? 'Applying your best match…'
          : 'Updating your Love Color…'
      );

      try {
        await this.selectRebuyProduct(
          this.loveStep,
          product,
          this.selectedLove
        );

        this.selectedLove = product;
        this.loveSelect.value = product.title;

        const recommendation = this.getRecommendation(
          this.selectedClassic?.title
        );

        this.renderLoveCard(product, recommendation);
        this.renderSummary();

        await sleep(REBUY_SETTLE_MS);
        this.syncFromRebuy();
      } finally {
        this.isSyncing = false;
      }
    }

    releaseOrphanedBundleScrollLock() {
      if (!window.matchMedia('(max-width: 989px)').matches) return;

      const legitimateOverlay = document.querySelector(
        [
          'header-drawer details[open]',
          'product-modal[open]',
          'product-modal.is-active',
          '.product-gallery.is-active',
          'modal-dialog[open]',
          'details-modal[open]',
          'cart-drawer[open]',
          'cart-drawer.active',
        ].join(',')
      );

      if (legitimateOverlay) return;

      const rebuyModalCandidates = document.querySelectorAll(
        [
          '.rebuy-modal',
          '.rebuy-modal-container',
          '.rebuy-modal-backdrop',
          '.rebuy-backdrop',
          '.rebuy-overlay',
        ].join(',')
      );

      const visibleRebuyModal = [...rebuyModalCandidates].some((element) => {
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          Number.parseFloat(style.opacity || '1') > 0 &&
          rect.width > 0 &&
          rect.height > 0
        );
      });

      if (visibleRebuyModal) return;

      if (
        window.bodyScrollLock &&
        typeof window.bodyScrollLock.clearAllBodyScrollLocks === 'function'
      ) {
        window.bodyScrollLock.clearAllBodyScrollLocks();
      }

      document.documentElement.classList.remove(
        'overflow-hidden',
        'overflow-hidden-mobile'
      );

      document.body.classList.remove(
        'overflow-hidden',
        'overflow-hidden-mobile',
        'modal-open'
      );

      if (document.documentElement.style.overflow === 'hidden') {
        document.documentElement.style.removeProperty('overflow');
      }

      if (document.body.style.overflow === 'hidden') {
        document.body.style.removeProperty('overflow');
      }

      if (document.documentElement.style.touchAction === 'none') {
        document.documentElement.style.removeProperty('touch-action');
      }

      if (document.body.style.touchAction === 'none') {
        document.body.style.removeProperty('touch-action');
      }
    }

    async selectRebuyProduct(step, targetProduct, previousProduct) {
      if (!step || !targetProduct) return;

      if (
        previousProduct &&
        normalizeTitle(previousProduct.title) ===
          normalizeTitle(targetProduct.title)
      ) {
        return;
      }

      if (previousProduct) {
        await this.removeRebuyProduct(previousProduct);
      }

      this.refreshProductAction(targetProduct);

      const addButton = this.findAddButton(targetProduct);

      if (!addButton) {
        console.warn(
          `[MP Bundle] Add button not found for ${targetProduct.title}.`
        );
        return;
      }

      addButton.click();
      await sleep(REBUY_SETTLE_MS);
      this.refreshAllProductActions();
      this.releaseOrphanedBundleScrollLock();
    }

    async removeRebuyProduct(product) {
      if (!product) return;

      this.refreshProductAction(product);
      const removeButton = this.findRemoveButton(product);

      if (removeButton) {
        removeButton.click();
        await sleep(REBUY_SETTLE_MS);
        this.releaseOrphanedBundleScrollLock();
        return;
      }

      /*
       * Some Rebuy configurations replace the existing product automatically
       * when the step maximum is one. In that case, no explicit removal is
       * necessary and the target Add button is clicked next.
       */
    }

    refreshAllProductActions() {
      [...this.classicProducts, ...this.loveProducts].forEach((product) => {
        this.refreshProductAction(product);
      });

      this.captureRebuyElements();
    }

    refreshProductAction(product) {
      if (!product?.element) return;

      product.addButton = product.element.querySelector(
        '.rebuy-bundle-builder__product-actions button.rebuy-button'
      );
    }

    findAddButton(product) {
      const buttons = [
        ...product.element.querySelectorAll(
          '.rebuy-bundle-builder__product-actions button'
        ),
      ];

      return (
        buttons.find((button) => {
          const label = `${button.getAttribute('aria-label') || ''} ${
            button.textContent || ''
          }`.toLowerCase();

          return (
            !button.disabled &&
            (label.includes('add the product') ||
              label.includes('add to bundle') ||
              label.includes('add'))
          );
        }) || null
      );
    }

    findRemoveButton(product) {
      const buttons = [
        ...product.element.querySelectorAll(
          '.rebuy-bundle-builder__product-actions button'
        ),
      ];

      return (
        buttons.find((button) => {
          const label = `${button.getAttribute('aria-label') || ''} ${
            button.textContent || ''
          }`.toLowerCase();

          return (
            !button.disabled &&
            (label.includes('remove') ||
              label.includes('decrease') ||
              label.includes('minus'))
          );
        }) || null
      );
    }

    findProduct(products, title) {
      const target = normalizeTitle(title);
      return products.find((product) => product.key === target) || null;
    }

getRecommendation(classicTitle) {
  const target = normalizeTitle(classicTitle);

  const activeWorld =
    this.config.worlds?.[this.config.worldKey];

  const classics = activeWorld?.classics || {};

  const entry = Object.entries(classics).find(
    ([configuredTitle]) =>
      normalizeTitle(configuredTitle) === target
  );

  if (!entry) {
    console.warn(
      `[MP Bundle] No recommendation found for "${classicTitle}" ` +
      `in bundle world "${this.config.worldKey}".`
    );

    return null;
  }

  return {
    configuredTitle: entry[0],
    ...entry[1],
  };
}

    renderClassicCard(product) {
      const recommendation = this.getRecommendation(product.title);

      if (!recommendation || !this.classicCard) return;

      this.classicCard.hidden = false;
      this.classicImage.src = product.image;
      this.classicImage.alt = product.imageAlt || product.title;
      this.classicTitle.textContent = product.title;
      this.classicDescriptor.textContent =
        recommendation.descriptor || '';

      this.renderChips(recommendation.chips || []);
    }

    renderChips(chips) {
      if (!this.classicChips) return;

      this.classicChips.innerHTML = '';

      chips.forEach((chip) => {
        const element = document.createElement('span');
        element.className = 'mp-color-care-bundle__chip';
        element.textContent = chip;
        this.classicChips.appendChild(element);
      });
    }

    renderLoveCard(product, recommendation) {
      if (!product || !recommendation || !this.loveCard) return;

      const isBestMatch =
        normalizeTitle(product.title) === normalizeTitle(recommendation.match);

      const isAlternate =
        recommendation.alternate &&
        normalizeTitle(product.title) ===
          normalizeTitle(recommendation.alternate);

      this.loveCard.hidden = false;
      this.loveImage.src = product.image;
      this.loveImage.alt = product.imageAlt || product.title;
      this.loveTitle.textContent = product.title;

 if (isBestMatch) {
  this.matchBadge.textContent = 'Best Match';
  this.loveDescriptor.textContent =
    `Recommended for ${recommendation.descriptor}`;
} else if (isAlternate) {
  this.matchBadge.textContent = 'Brightening Alternative';
  this.loveDescriptor.textContent =
    'Tone-enhancing Love Color™';
} else {
  this.matchBadge.textContent = 'Custom Match';
  this.loveDescriptor.textContent =
    'Your selected Love Color™';
}
    }

    renderSummary() {
      if (!this.summaryProducts) return;

      if (!this.selectedClassic && !this.selectedLove) {
        this.summaryProducts.innerHTML = `
          <div class="mp-color-care-bundle__summary-placeholder">
            Choose one Classic shade and one Love Color.
          </div>
        `;
        return;
      }

      const products = [this.selectedClassic, this.selectedLove].filter(Boolean);

      this.summaryProducts.innerHTML = products
        .map(
          (product) => `
            <div class="mp-color-care-bundle__summary-item">
              <img
                src="${this.escapeHTML(product.image)}"
                alt="${this.escapeHTML(product.imageAlt || product.title)}"
                width="52"
                height="52"
                loading="lazy"
              >
              <div>
                <p class="mp-color-care-bundle__summary-item-title">
                  ${this.escapeHTML(product.title)}
                </p>
                <p class="mp-color-care-bundle__summary-item-type">
                  ${this.escapeHTML(product.typeLabel)}
                </p>
              </div>
              <span class="mp-color-care-bundle__summary-item-price">
                ${this.escapeHTML(product.priceText || formatMoney(product.price))}
              </span>
            </div>
          `
        )
        .join('');

      this.renderFallbackPricing();
    }

renderFallbackPricing() {
  const products = [this.selectedClassic, this.selectedLove].filter(Boolean);

  const regularTotal = products.reduce(
    (sum, product) => sum + (product.price || 0),
    0
  );

  const compareTotal = products.reduce(
    (sum, product) =>
      sum + (product.compareAtPrice || product.price || 0),
    0
  );
if (products.length === 2 && regularTotal > 0) {
const discounted =
  compareTotal * (1 - this.getDiscountPercent() / 100);

const rebuyRoundedPrice =
  Math.round(discounted * 10) / 10;

this.bundlePrice.textContent = formatMoney(rebuyRoundedPrice);

    const compareText = this.comparePrice?.querySelector('s');

    if (compareText) {
      compareText.textContent = formatMoney(compareTotal);
    }

    if (this.comparePrice) {
      this.comparePrice.hidden = false;
    }
  } else {
    this.bundlePrice.textContent = '—';

    if (this.comparePrice) {
      this.comparePrice.hidden = true;
    }
  }
}

    syncFromRebuy() {
      if (!this.builder) return;

      this.captureRebuyElements();
      this.syncPricingFromRebuy();

      const selectedCount = this.getRebuySelectedCount();
      const complete =
        selectedCount >= 2 &&
        this.selectedClassic &&
        this.selectedLove &&
        this.rebuyCTA &&
        !this.rebuyCTA.disabled;

      this.customCTA.disabled = !complete;

      if (complete) {
        this.setStatus(
          `Your bundle is complete. The ${this.getDiscountPercent()}% savings will be applied automatically.`
        );
      } else if (this.selectedClassic && this.selectedLove) {
        this.setStatus('Finishing your bundle…');
      } else {
        this.setStatus(
          'Select one product from each step to complete your bundle.'
        );
      }
    }

    syncPricingFromRebuy() {
      if (!this.rebuySummary) {
        this.renderFallbackPricing();
        return;
      }

      const saleMoney = this.findSummaryMoney('.rebuy-money.sale');
      const compareMoney = this.findSummaryMoney('.rebuy-money.compare-at');

      if (saleMoney) {
  const saleAmount = Number(
    saleMoney.replace(/[^0-9.-]+/g, '')
  );

  this.bundlePrice.textContent = Number.isFinite(saleAmount)
    ? formatMoney(Math.round(saleAmount * 10) / 10)
    : saleMoney;

        if (compareMoney && this.comparePrice) {
          const compareText = this.comparePrice.querySelector('s');
          if (compareText) compareText.textContent = compareMoney;
          this.comparePrice.hidden = false;
        }

        return;
      }

      const ctaText = this.rebuyCTA?.textContent || '';
      const currencyMatches = ctaText.match(/\$[\d,.]+/g);

      if (currencyMatches?.length) {
        this.bundlePrice.textContent =
          currencyMatches[currencyMatches.length - 1];
        return;
      }

      this.renderFallbackPricing();
    }

    findSummaryMoney(selector) {
      const elements = [...this.rebuySummary.querySelectorAll(selector)];

      const visibleText = elements
        .map((element) => element.textContent.replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      return visibleText.length
        ? visibleText[visibleText.length - 1].match(/\$[\d,.]+/)?.[0] || null
        : null;
    }

    getRebuySelectedCount() {
      const countElement = this.rebuySummary?.querySelector(
        '.rebuy-bundle-builder__group-container-header-products_count'
      );

      const count = Number.parseInt(countElement?.textContent || '0', 10);
      return Number.isFinite(count) ? count : 0;
    }

  async handleCustomCTA() {
  if (
    !this.rebuyCTA ||
    this.rebuyCTA.disabled ||
    this.isSyncing
  ) {
    return;
  }

  this.isSyncing = true;
  this.customCTA.disabled = true;
  this.setStatus('Adding your Color Care Bundle…');

  try {
    const cartBefore = await this.getCartState();
    const itemCountBefore = cartBefore?.item_count ?? 0;

    this.rebuyCTA.click();

    const addedSuccessfully = await this.waitForCartIncrease(
      itemCountBefore,
      8000
    );

    if (!addedSuccessfully) {
      this.setStatus(
        'Your bundle may still be processing. Please check your cart before trying again.'
      );
      return;
    }

    await this.resetBundleBuilder();

    this.setStatus(
      'Your bundle was added to cart. Build another bundle or continue shopping.'
    );
  } catch (error) {
    console.error(
      '[MP Bundle] Add-to-cart confirmation failed.',
      error
    );

    this.setStatus(
      'Please check your cart to confirm the bundle was added.'
    );
  } finally {
    this.isSyncing = false;
    this.releaseOrphanedBundleScrollLock();
  }

    }
    async getCartState() {
  const root =
    window.Shopify?.routes?.root || '/';

  const response = await fetch(`${root}cart.js`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(
      `Cart request failed with status ${response.status}.`
    );
  }

  return response.json();
}

async waitForCartIncrease(
  startingItemCount,
  timeoutMilliseconds = 8000
) {
  const startedAt = Date.now();

  while (
    Date.now() - startedAt <
    timeoutMilliseconds
  ) {
    await sleep(300);

    try {
      const cart = await this.getCartState();

      if (
        Number(cart?.item_count || 0) >
        Number(startingItemCount || 0)
      ) {
        return true;
      }
    } catch (error) {
      console.warn(
        '[MP Bundle] Waiting for cart update.',
        error
      );
    }
  }

  return false;
}

async resetBundleBuilder() {
  /*
   * Remove the products from the hidden Rebuy builder first.
   * This prevents the next bundle from inheriting the old selections.
   */
  const selectedProducts = [
    this.selectedClassic,
    this.selectedLove,
  ].filter(Boolean);

  for (const product of selectedProducts) {
    await this.removeRebuyProduct(product);
  }

  this.selectedClassic = null;
  this.selectedLove = null;
  this.recommendedLoveTitle = '';
  if (this.matchHeading) {
  this.matchHeading.textContent = 'Recommended Match Appears Here';
}

  this.classicSelect.value = '';

  this.populateLoveSelector();
  this.loveSelect.value = '';
  this.loveSelect.disabled = true;

  if (this.classicCard) {
    this.classicCard.hidden = true;
  }

  if (this.loveCard) {
    this.loveCard.hidden = true;
  }

  if (this.classicChips) {
    this.classicChips.innerHTML = '';
  }

  this.renderSummary();
  this.renderFallbackPricing();

  this.customCTA.disabled = true;

  await sleep(REBUY_SETTLE_MS);

  this.refreshAllProductActions();
  this.syncFromRebuy();
}

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    escapeHTML(value = '') {
      const element = document.createElement('div');
      element.textContent = String(value);
      return element.innerHTML;
    }

    destroy() {
      this.observer?.disconnect();

      this.classicSelect?.removeEventListener(
        'change',
        this.handleClassicChange
      );

      this.loveSelect?.removeEventListener('change', this.handleLoveChange);
      this.customCTA?.removeEventListener('click', this.handleCustomCTA);
    }
  }

  const initialize = (root = document) => {
    const sections = root.matches?.(SECTION_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(SECTION_SELECTOR)];

    sections.forEach((section) => {
      if (section.dataset.mpBundleV2Initialized === 'true') return;

      section.dataset.mpBundleV2Initialized = 'true';

      const controller = new ColorCareBundleController(section);
      section.mpColorCareBundleController = controller;
      controller.initialize();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector?.(SECTION_SELECTOR);

    section?.mpColorCareBundleController?.destroy();
  });

  [
    'rebuy:bundle-builder.ready',
    'rebuy:widget.ready',
    'rebuy.ready',
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => initialize());
  });
})();